import { useCallback, useEffect, useRef, useState } from 'react';

import { isNativeShell, nativeCaps, onNativeSpeech, postToNative } from '../lib/nativeBridge';

function getRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/**
 * Voice is available either through the browser's Web Speech API or, inside the
 * packaged app, through native recognition bridged from the shell. No WebView
 * implements Web Speech, so without the bridge the button would be dead.
 */
export function isSpeechSupported(): boolean {
  if (isNativeShell()) return nativeCaps()?.speech === true;
  return !!getRecognitionCtor();
}

/** Kept for call sites that only need the answer once, at render time. */
export const speechSupported = isSpeechSupported();

interface SpeechState {
  listening: boolean;
  /** Everything recognised so far this session. */
  transcript: string;
  /** The in-flight phrase, shown live so the user can see it working. */
  interim: string;
  error: string | null;
  /**
   * How many times the engine has been restarted inside this take.
   *
   * Surfaced because a take ending on its own and a take being restarted look
   * identical from outside, and telling them apart is the difference between
   * "recording is broken" and "recording is working as designed".
   */
  restarts: number;
}

export function useSpeech() {
  const [state, setState] = useState<SpeechState>({
    listening: false,
    transcript: '',
    interim: '',
    error: null,
    restarts: 0,
  });
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalRef = useRef('');
  /**
   * Text from earlier sessions inside this same take.
   *
   * The engine is restarted repeatedly through a long dictation, and each
   * restart begins a fresh `results` list. This holds everything recognised
   * before the current session so the take can be rebuilt as
   * committed + current session, without ever appending the same phrase twice.
   */
  const committedRef = useRef('');
  /**
   * Browsers stop recognition after a pause, and Android does the same. The user
   * decides when a recording ends, so this tracks intent and the session is
   * restarted underneath them until they actually press stop.
   */
  const wantListeningRef = useRef(false);
  /**
   * True only for a take the user actually began. The engine is restarted many
   * times inside one take, and each restart fires `onstart` again — without this
   * every restart wiped the transcript, so a pause longer than a few seconds
   * made everything said so far disappear from the screen.
   */
  const freshTakeRef = useRef(true);
  /** Pending restart timer, so stopping cancels a restart already scheduled. */
  const restartTimerRef = useRef<number | null>(null);

  /**
   * Stop is authoritative: the UI leaves the recording state on this call, not
   * on an engine event.
   *
   * The engine is restarted repeatedly inside one take, so a stop landing in the
   * gap between sessions used to be swallowed — `stop()` reached a recogniser
   * that had already ended, no further `onend` arrived, and the screen sat on
   * "Recording" with a Stop button that did nothing. The transcript is flushed
   * from finalRef here for the same reason: whatever was said is already in it,
   * and waiting for a final event that may never come loses the take.
   */
  const stop = useCallback(() => {
    wantListeningRef.current = false;
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }

    setState((s) => ({
      ...s,
      listening: false,
      // Fold the in-flight phrase in rather than dropping it — the engine often
      // has a good interim reading that it never gets to finalise.
      transcript: (finalRef.current.trim() || s.transcript || s.interim).trim(),
      interim: '',
    }));

    if (isNativeShell()) {
      postToNative({ type: 'speech-stop' });
      return;
    }
    try {
      recognitionRef.current?.stop();
    } catch {
      // Already stopped. The state above is what the user sees either way.
    }
  }, []);

  const reset = useCallback(() => {
    wantListeningRef.current = false;
    freshTakeRef.current = true;
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    finalRef.current = '';
    committedRef.current = '';
    setState({ listening: false, transcript: '', interim: '', error: null, restarts: 0 });
  }, []);

  const start = useCallback(() => {
    // The shell recognises speech natively and streams events back through the
    // bridge, so the rest of this hook behaves identically either way.
    if (isNativeShell()) {
      wantListeningRef.current = true;
      freshTakeRef.current = true;
      finalRef.current = '';
      setState({ listening: true, transcript: '', interim: '', error: null, restarts: 0 });
      if (!postToNative({ type: 'speech-start' })) {
        setState((s) => ({ ...s, listening: false, error: 'Could not reach the microphone.' }));
      }
      return;
    }

    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setState((s) => ({ ...s, error: 'This browser has no speech recognition. Type the values instead.' }));
      return;
    }

    recognitionRef.current?.abort();
    recognitionRef.current = null;
    finalRef.current = '';
    committedRef.current = '';
    wantListeningRef.current = true;
    freshTakeRef.current = true;

    const recognition = new Ctor();
    // en-SG first; the browser falls back to its own default if unsupported.
    recognition.lang = 'en-SG';
    // Keep going through pauses. Dictating several readings involves thinking
    // between them, and a recorder that stops at the first silence is useless.
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      if (recognitionRef.current !== recognition) return;
      // Only a take the user began clears what is on screen. A restart mid-take
      // must leave the transcript alone, or a pause erases everything so far.
      if (freshTakeRef.current) {
        freshTakeRef.current = false;
        setState({ listening: true, transcript: '', interim: '', error: null, restarts: 0 });
        return;
      }
      setState((s) => ({ ...s, listening: true, error: null, restarts: s.restarts + 1 }));
    };

    recognition.onresult = (event) => {
      // Events from a recogniser we have already replaced must not write state.
      if (recognitionRef.current !== recognition) return;

      /**
       * Rebuilt from scratch on every event, never appended to.
       *
       * `event.results` is cumulative for the session, and the engine re-fires
       * results it has already marked final — `event.resultIndex` is not a
       * reliable "only what is new" marker. Appending from it made a phrase
       * arrive two or three times over. Rebuilding is idempotent: however many
       * times a result is re-delivered, it appears once.
       */
      let sessionFinal = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) sessionFinal += `${text} `;
        else interim += text;
      }

      finalRef.current = `${committedRef.current} ${sessionFinal}`.replace(/\s+/g, ' ').trim();
      setState((s) => ({ ...s, transcript: finalRef.current, interim: interim.trim() }));
    };

    recognition.onerror = (event) => {
      // A recogniser we replaced reports 'aborted' as it dies; that is our own
      // doing and must not tear down the take that replaced it.
      if (recognitionRef.current !== recognition) return;

      /**
       * Only a genuinely fatal error ends a take the user has not stopped.
       *
       * This used to end on anything but 'no-speech', and Chrome emits
       * 'aborted' routinely around its own restart cycles — mapped to an empty
       * message, so recording stopped dead with nothing on screen explaining
       * why. 'network' is the same story: Chrome's recogniser is server-backed
       * and a brief blip is not a reason to throw away a dictation in progress.
       * These are left to `onend`, which restarts and carries the text over.
       *
       * Fatal means the microphone is genuinely unavailable — no permission, no
       * device, or the service refusing us. Those cannot be retried around.
       */
      const FATAL = ['not-allowed', 'service-not-allowed', 'audio-capture'];
      if (!FATAL.includes(event.error) && wantListeningRef.current) return;

      wantListeningRef.current = false;
      setState((s) => ({
        ...s,
        listening: false,
        // Never lose the take to an error — whatever was recognised stands.
        transcript: finalRef.current.trim() || s.transcript,
        interim: '',
        error: describeSpeechError(event.error),
      }));
    };

    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      /**
       * The engine stops itself after a few seconds of silence — that is the
       * whole reason a take seemed to last about five seconds. Restart while the
       * user still intends to be recording, so a thinking pause does not end it.
       *
       * The restart must not be synchronous: Chrome is still tearing the session
       * down when `onend` fires, so calling start() here throws InvalidStateError
       * and the take really does end. A short turn of the event loop lets the
       * engine release first.
       */
      if (wantListeningRef.current) {
        // Bank this session's text. The next session's `results` starts empty,
        // so without this the restart would drop everything said so far.
        committedRef.current = finalRef.current;
        restartTimerRef.current = window.setTimeout(() => {
          restartTimerRef.current = null;
          if (!wantListeningRef.current) return;
          try {
            recognition.start();
          } catch {
            // Genuinely cannot resume — surface the stop rather than looping,
            // keeping everything recognised so far.
            wantListeningRef.current = false;
            setState((s) => ({
              ...s,
              listening: false,
              transcript: finalRef.current.trim() || s.transcript,
              interim: '',
            }));
          }
        }, 250);
        return;
      }
      setState((s) => ({
        ...s,
        listening: false,
        transcript: finalRef.current.trim() || s.transcript,
        interim: '',
      }));
    };

    try {
      // Claimed before starting, so the handlers' staleness guard sees this as
      // the live recogniser from its very first event.
      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setState((s) => ({ ...s, error: 'Could not start the microphone.' }));
    }
  }, []);

  useEffect(
    () => () => {
      wantListeningRef.current = false;
      if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
      recognitionRef.current?.abort();
    },
    [],
  );

  // Native recognition events, shaped into the same state the browser path sets.
  useEffect(() => {
    if (!isNativeShell()) return undefined;
    return onNativeSpeech((e) => {
      if (e.event === 'start') {
        // A native restart mid-take must not wipe what was already recognised.
        if (!freshTakeRef.current) {
          setState((s) => ({ ...s, listening: true, error: null, restarts: s.restarts + 1 }));
          return;
        }
        freshTakeRef.current = false;
        finalRef.current = '';
        committedRef.current = '';
        setState({ listening: true, transcript: '', interim: '', error: null, restarts: 0 });
        return;
      }
      if (e.event === 'result') {
        if (e.isFinal) finalRef.current = e.transcript ?? finalRef.current;
        setState((s) => ({
          ...s,
          listening: true,
          transcript: (e.isFinal ? e.transcript : e.transcript) ?? s.transcript,
          interim: e.interim ?? '',
        }));
        return;
      }
      if (e.event === 'error') {
        setState((s) => ({
          ...s,
          listening: false,
          error: describeSpeechError(e.code ?? 'unknown') || (e.message ?? ''),
        }));
        return;
      }
      if (e.event === 'end') {
        // The native recogniser also stops on silence; only surface the stop
        // when the user asked for it.
        if (wantListeningRef.current) {
          postToNative({ type: 'speech-restart' });
          return;
        }
        setState((s) => ({
          ...s,
          listening: false,
          transcript: e.transcript || finalRef.current || s.transcript,
          interim: '',
        }));
      }
    });
  }, []);

  return { ...state, start, stop, reset, supported: isSpeechSupported() };
}

function describeSpeechError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission denied. Allow it in the browser, or type the values.';
    case 'no-speech':
      return 'Nothing heard. Try again, closer to the microphone.';
    case 'audio-capture':
      return 'No microphone found.';
    case 'network':
      return 'Speech recognition needs a connection. Type the values instead.';
    case 'aborted':
      return '';
    default:
      return `Speech recognition failed (${code}).`;
  }
}

/**
 * en-SG lab speech tidy-up before the transcript goes to the model.
 * Deliberately conservative: it normalises obvious spoken forms and leaves
 * anything ambiguous alone for the model — and ultimately the human — to judge.
 */
export function tidyTranscript(raw: string): string {
  return raw
    .replace(/\bnought\b/gi, 'zero')
    .replace(/\bnegative\b/gi, 'minus')
    .replace(/\s+/g, ' ')
    .trim();
}
